import React, { ForwardedRef } from 'react';
import styled, { css } from 'styled-components';
import { spacing as globalSpacing } from '../../style/spacing';

// table components
import { BoxProps, Div } from '../box';
import { colors, fonts } from '../../style';
import TableRow, { TableRowProps } from './table-row';
import { TableHead, TableHeadProps } from './table-head';
import TableCell, {
  AvatarTextTableCell,
  AvatarTextTableCellProps,
  MultiSelectorTableHeadCell,
  MultiSelectorTableHeadCellProps,
  SortableTableHeadCell,
  SortableTableHeadCellProps,
  TableCellProps,
  TableHeadCell,
  TableHeadCellProps,
} from './table-cell/table-cell';
import { spacing } from './table-cell/table-cell-constants';
import { ClickableTableCell, ClickableTableCellProps } from './table-cell/clickable-table-cell';

export { clickableTableCellVariants } from './table-cell/clickable-table-cell-constants';

export type TableVariant = 'default' | 'grid-table';

interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  cellPadding?: keyof typeof spacing;
  variant?: TableVariant;
}

const tableHeadCellSpacing = (props: TableProps): any => css`
  padding-left: ${props.cellPadding && spacing[props.cellPadding]};
  padding-right: ${props.cellPadding && spacing[props.cellPadding]};
  padding-top: ${spacing.sm};
  padding-bottom: ${spacing.sm};
`;

const tableVariants: Record<TableVariant, ReturnType<typeof css>> = {
  default: css``,
  'grid-table': css`
    background: ${colors.white};
    thead th,
    thead td {
      padding-left: ${globalSpacing.sm};
      border-right: 1px solid ${colors.gray400};
      border-bottom: 1px solid ${colors.gray600};
      color: ${colors.gray900};
      background-color: ${colors.gray100};

      .box-text {
        font-family: ${fonts.robotoMono};
        font-size: 10px;
        letter-spacing: 1px;
      }
    }

    tbody td {
      border-right: 1px solid ${colors.gray400};
      padding-left: ${globalSpacing.sm};
    }

    tbody tr {
      border-bottom: 1px solid ${colors.gray400};
    }
  `,
};

const StyledTable = styled.table.withConfig({
  shouldForwardProp: prop => !['cellPadding', 'variant'].includes(prop),
})<TableProps>`
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  position: relative;

  ${props => props.variant && tableVariants[props.variant]}
  td {
    padding: ${props => props.cellPadding && spacing[props.cellPadding]};

    &:first-child {
      padding-left: ${props => (props.cellPadding ? spacing[props.cellPadding] : spacing.sm)};
    }
  }

  tr.disabled > td {
    background-color: ${colors.gray100};
    color: ${colors.gray600};
  }

  thead td, thead th {
    ${props => props.cellPadding && tableHeadCellSpacing(props)}
  }
}

tbody {
  position: relative;
}

`;

interface ScrollableTableProps {
  maxHeight?: React.CSSProperties['maxHeight'];
  height?: React.CSSProperties['height'];
  children?: React.ReactNode;
  paddingRight?: 'xs' | 'none';
  position?: 'relative';
  minHeight?: BoxProps['minHeight'];
  ref?: React.RefObject<any>;
  id?: string;
}

const StyledScrollableContainer = styled(Div)<BoxProps>`
  /* Make scrollbar overlay the content */

  & {
    scrollbar-width: thin; /* For modern browsers, make scrollbar thinner */
    scrollbar-color: #ccc transparent; /* Set thumb and track colors */
  }

  &::-webkit-scrollbar {
    width: 8px; /* Set scrollbar width */
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ccc; /* Scrollbar thumb color */
    border-radius: 4px; /* Rounded scrollbar */
  }

  &::-webkit-scrollbar-track {
    background-color: transparent; /* Transparent track to overlay content */
  }
`;

const ScrollableTableContainer = (props: ScrollableTableProps) => {
  const { height = 'auto', paddingRight = 'xs', children, position, minHeight, maxHeight, ref, id } = props;
  return (
    <StyledScrollableContainer
      minHeight={minHeight}
      maxHeight={maxHeight}
      height={height}
      overflowY={'auto'}
      ref={ref}
      position={position}
      id={id}
      style={{ paddingRight: globalSpacing[paddingRight] }}
      {...props}
    >
      {children}
    </StyledScrollableContainer>
  );
};

ScrollableTableContainer.displayName = 'ScrollableTableContainer';

type TableBodyProps = React.HTMLAttributes<HTMLTableSectionElement>;
export const TableBody = (props: TableBodyProps) => <tbody {...props} />;

TableBody.displayName = 'TableBody';

const Table = ((props: TableProps) => <StyledTable {...props} />) as React.FunctionComponent<TableProps> & {
  TableCell: React.FunctionComponent<TableCellProps>;
  ClickableTableCell: React.FunctionComponent<ClickableTableCellProps>;
  TableBody: React.FunctionComponent<TableBodyProps>;
  TableHead: React.FunctionComponent<TableHeadProps>;
  TableHeadCell: React.FunctionComponent<TableHeadCellProps>;
  AvatarTextTableCell: React.FunctionComponent<AvatarTextTableCellProps>;
  SortableTableHeadCell: React.FunctionComponent<SortableTableHeadCellProps>;
  TableRow: React.FunctionComponent<TableRowProps>;
  ScrollableTableContainer: React.FunctionComponent<ScrollableTableProps & { ref?: ForwardedRef<any> }>;
  MultiSelectorTableHeadCell: React.FunctionComponent<MultiSelectorTableHeadCellProps>;
};

// Attach subcomponents
Table.TableCell = (props: TableCellProps) => <TableCell {...props} />;
Table.ClickableTableCell = (props: ClickableTableCellProps) => <ClickableTableCell {...props} />;
Table.TableBody = (props: TableBodyProps) => <TableBody {...props} />;
Table.TableHead = (props: TableHeadProps) => <TableHead {...props} />;
Table.TableHeadCell = (props: TableHeadCellProps) => <TableHeadCell {...props} />;
Table.AvatarTextTableCell = (props: AvatarTextTableCellProps) => <AvatarTextTableCell {...props} />;
Table.SortableTableHeadCell = (props: SortableTableHeadCellProps) => <SortableTableHeadCell {...props} />;
Table.TableRow = (props: TableRowProps) => <TableRow {...props} />;
Table.ScrollableTableContainer = (props: ScrollableTableProps) => <ScrollableTableContainer {...props} />;

Table.MultiSelectorTableHeadCell = (props: MultiSelectorTableHeadCellProps) => (
  <MultiSelectorTableHeadCell {...props} />
);

export default Table;
